import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  createCheckoutSession,
  createInvoiceSubscription,
  createPlanChangeEstimate,
  createPlanChangeSchedule,
  type PaidCatalogGrade,
  type PaidCatalogResponse,
  type PlanChangeEstimateResponse,
  type ServiceSubscriptionSummary,
} from "@/api/service-subscription";
import { ApiError, apiErrorMessage } from "@/api/client";
import { Button } from "@/components/ui/button";
import { formatDay } from "./ServiceCancellationDialog";

type BillingCycle = "monthly" | "yearly";
type PaymentMethod = "card" | "invoice";

const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  card: "カード",
  invoice: "請求書払い（銀行振込・コンビニ）",
};

const SCHEDULED_KINDS = new Set<PlanChangeEstimateResponse["changeKind"]>([
  "scheduled_upgrade",
  "scheduled_downgrade",
  "cycle_to_monthly",
]);

const CHANGE_LABELS: Record<PlanChangeEstimateResponse["changeKind"], string> = {
  initial_paid_subscription: "有料プランのお申込み",
  immediate_upgrade: "プランを今すぐ変更する",
  scheduled_upgrade: "次回更新時にプランを変更する",
  scheduled_downgrade: "次回更新時にプランを変更する",
  cycle_to_yearly: "年払いへ切り替える",
  cycle_to_monthly: "次回更新時に月払いへ切り替える",
};

const ESTIMATE_ERRORS: Record<string, string> = {
  cycle_change_grade_mismatch: "kintoneコース・アプリ数と請求周期は同時に変更できません。どちらか一方ずつ変更してください。",
  target_app_limit_exceeded: "登録済みのアプリ数より少ないプランは選べません。",
  scheduled_change_conflict: "予約中の変更があります。予約を取り消してから選び直してください。",
  billing_period_ending: "更新時刻が近いため、更新後に改めてお申し込みください。",
  billing_estimate_required: "料金を再見積りしてください。",
  billing_estimate_expired: "見積りの有効期限が切れました。プランを選び直して料金をご確認ください。",
  billing_estimate_changed: "契約または料金が変わりました。プランを選び直して料金をご確認ください。",
  checkout_session_already_pending: "決済画面へ進んだお申込みが完了していません。決済を完了するか中止してください。",
  invoice_payment_denied: "請求書払いはご利用いただけません。カードでお申し込みください。",
  invoice_unpaid_blocks_change: "未払いの請求書があるため、お支払い後に変更できます。",
  payment_method_switch_unsupported: "ご契約中のお支払い方法は変更できません。今のお支払い方法でお申し込みください。",
};

interface PaidPlanSelectorProps {
  subscription: ServiceSubscriptionSummary;
  catalog: PaidCatalogResponse;
  onChanged: () => Promise<void>;
}

export function PaidPlanSelector({ subscription, catalog, onChanged }: PaidPlanSelectorProps) {
  const billing = subscription.billingSubscriptionSummary;
  const currentCycle = billing?.billingCycle ?? null;
  const isPaid = Boolean(subscription.planKind === "paid" && billing?.recurringBillingActive);
  // D-1: a running contract keeps its payment method; a new contract may choose
  // invoice payment unless the organization cannot use it.
  const onInvoiceContract = isPaid && billing?.collectionMethod === "send_invoice";
  const invoiceOffered = !isPaid && catalog.invoicePayment.available;
  const registered = subscription.registeredKintoneAppCount;
  const selectable = (grade: PaidCatalogGrade) => grade.appLimit >= registered;
  const [cycle, setCycle] = useState<BillingCycle>(currentCycle ?? "monthly");
  const [kintonePlan, setKintonePlan] = useState<PaidCatalogGrade["kintonePlan"] | null>(
    () => catalog.grades.find((grade) => grade.gradeCode === subscription.paidGrade)?.kintonePlan ?? null,
  );
  const [gradeCode, setGradeCode] = useState<string | null>(
    () => catalog.grades.find((grade) => grade.gradeCode === subscription.paidGrade)?.gradeCode ?? null,
  );
  const [estimate, setEstimate] = useState<PlanChangeEstimateResponse | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [chosenMethod, setChosenMethod] = useState<PaymentMethod>("card");
  const [notice, setNotice] = useState<string | null>(null);
  const requestSeq = useRef(0);
  const method: PaymentMethod = onInvoiceContract ? "invoice" : invoiceOffered ? chosenMethod : "card";

  const isCurrentPlan = Boolean(isPaid && gradeCode === subscription.paidGrade && cycle === currentCycle);
  const selectedGrade = catalog.grades.find((grade) => grade.gradeCode === gradeCode) ?? null;

  useEffect(() => {
    setConfirming(false);
    setAgreed(false);
    setEstimate(null);
    setMessage(null);
    const seq = ++requestSeq.current;
    setEstimating(false);
    if (!gradeCode || isCurrentPlan) return;
    setEstimating(true);
    createPlanChangeEstimate({ targetGradeCode: gradeCode, targetBillingCycle: cycle })
      .then((result) => { if (seq === requestSeq.current) setEstimate(result); })
      .catch((cause: unknown) => {
        if (seq !== requestSeq.current) return;
        const code = cause instanceof ApiError ? cause.code : undefined;
        setMessage((code && ESTIMATE_ERRORS[code]) ?? apiErrorMessage(cause, "料金を見積れませんでした。"));
      })
      .finally(() => { if (seq === requestSeq.current) setEstimating(false); });
  }, [gradeCode, cycle, isCurrentPlan]);

  async function proceed() {
    if (!estimate || !gradeCode) return;
    setBusy(true);
    setMessage(null);
    setNotice(null);
    try {
      if (SCHEDULED_KINDS.has(estimate.changeKind)) {
        await createPlanChangeSchedule({ targetGradeCode: gradeCode, targetBillingCycle: cycle });
        await onChanged();
      } else if (method === "invoice") {
        await createInvoiceSubscription({
          targetGradeCode: gradeCode,
          targetBillingCycle: cycle,
          paidTermsVersion: catalog.paidTermsVersion,
          estimateId: estimate.estimateId,
        });
        setConfirming(false);
        setAgreed(false);
        setEstimate(null);
        setNotice("請求書を発行しました。新しいプランはすぐに使えます。請求書は「未払いの請求書」から開けます。");
        await onChanged();
      } else {
        const result = await createCheckoutSession({
          targetGradeCode: gradeCode,
          targetBillingCycle: cycle,
          returnUrl: `${window.location.origin}/console/billing/return`,
          paidTermsVersion: catalog.paidTermsVersion,
          estimateId: estimate.estimateId,
        });
        window.location.assign(result.checkoutUrl);
        return;
      }
    } catch (cause) {
      const code = cause instanceof ApiError ? cause.code : undefined;
      setMessage(code === "paid_terms_version_not_current"
        ? "有料プランの利用条件が更新されました。画面を再読込して、新しい条件をご確認ください。"
        : (code && ESTIMATE_ERRORS[code]) ?? apiErrorMessage(cause, "お申込みを開始できませんでした。"));
    }
    setBusy(false);
  }

  const scheduled = estimate ? SCHEDULED_KINDS.has(estimate.changeKind) : false;
  const recurringAmount = selectedGrade ? (cycle === "monthly" ? selectedGrade.monthlyAmountWithTax : selectedGrade.yearlyAmountWithTax) : null;
  const byInvoice = method === "invoice" && !scheduled;
  // One unpaid invoice at a time: the API refuses immediate changes until it is paid.
  const blockedByOpenInvoice = byInvoice && Boolean(billing?.openInvoice);
  const konbiniUnavailable = Boolean(byInvoice && estimate && estimate.chargeAmountWithTax > catalog.invoicePayment.konbiniMaxAmountWithTax);

  function selectKintonePlan(value: PaidCatalogGrade["kintonePlan"]) {
    setKintonePlan(value);
    const grades = catalog.grades.filter((grade) => grade.kintonePlan === value);
    const sameLimit = grades.find((grade) => grade.appLimit === selectedGrade?.appLimit && selectable(grade));
    setGradeCode((sameLimit ?? grades.find(selectable))?.gradeCode ?? null);
  }

  return (
    <div className="space-y-4">
      <fieldset className="space-y-2" aria-label="ご利用中のkintoneコース">
        <legend className="text-sm font-medium">ご利用中のkintoneコース（自己申告）</legend>
        <p className="text-sm text-muted-foreground">kintoneで契約中のコースを選んでください。選択したコースに応じて料金が変わります。</p>
        <div className="flex flex-wrap gap-3">
          {(["standard", "wide"] as const).map((value) => (
            <label key={value} className="flex items-center gap-2 text-sm">
              <input type="radio" name="kintone-plan" checked={kintonePlan === value} onChange={() => selectKintonePlan(value)} />
              {kintonePlanLabel(value)}
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset className="flex flex-wrap gap-2" aria-label="請求周期">
        {(["monthly", "yearly"] as const).map((value) => (
          <label key={value} className={`relative cursor-pointer rounded-md border px-3 py-1.5 text-sm ${cycle === value ? "border-primary bg-primary/10 font-medium" : ""}`}>
            <input type="radio" name="billing-cycle" value={value} className="sr-only" checked={cycle === value} onChange={() => setCycle(value)} />
            {value === "monthly" ? "月払い" : "年払い"}
          </label>
        ))}
      </fieldset>

      <div role="radiogroup" aria-label="プラン" className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {catalog.grades.filter((grade) => grade.kintonePlan === kintonePlan).map((grade) => {
          const disabled = !selectable(grade);
          const checked = gradeCode === grade.gradeCode;
          const yearlySaving = grade.monthlyAmountWithTax * 12 - grade.yearlyAmountWithTax;
          const current = isPaid && subscription.paidGrade === grade.gradeCode;
          return (
            <label
              key={grade.gradeCode}
              className={`block rounded-lg border p-3 text-sm ${disabled ? "cursor-not-allowed bg-muted/40 text-muted-foreground" : "cursor-pointer"} ${checked ? "border-primary ring-1 ring-primary" : ""}`}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="paid-grade"
                    value={grade.gradeCode}
                    checked={checked}
                    disabled={disabled}
                    onChange={() => setGradeCode(grade.gradeCode)}
                  />
                  <span className="text-base font-semibold">{grade.appLimit}アプリまで</span>
                </span>
                {current ? <span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary">現在のプラン</span> : null}
              </span>
              <span className="mt-2 block">
                {cycle === "monthly"
                  ? <>{yen(grade.monthlyAmountWithTax)} / 月（税込）</>
                  : <>{yen(grade.yearlyAmountWithTax)} / 年（税込）</>}
              </span>
              <span className="block text-xs text-muted-foreground">
                税別 {yen(cycle === "monthly" ? grade.monthlyAmount : grade.yearlyAmount)}
                {cycle === "yearly" && yearlySaving > 0 ? `・月払いより年間 ${yen(yearlySaving)} お得` : ""}
              </span>
              {disabled ? (
                <span className="mt-2 block text-xs">
                  登録済み {registered} アプリのため選べません。<Link to="/console/app-bindings" className="text-primary underline">アプリ一覧</Link>で減らすと選べます。
                </span>
              ) : null}
            </label>
          );
        })}
      </div>

      {notice ? <p role="status" className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">{notice}</p> : null}
      {isCurrentPlan ? <p className="text-sm text-muted-foreground">選んでいるのは現在のプランです。</p> : null}
      {estimating ? <p role="status" className="text-sm text-muted-foreground">料金を計算しています…</p> : null}
      {message ? <p role="alert" className="text-sm text-destructive">{message}</p> : null}

      {estimate && selectedGrade && !confirming ? (
        <div className="rounded-lg border bg-muted/30 p-4 text-sm">
          <p className="font-medium">{CHANGE_LABELS[estimate.changeKind]}</p>
          {scheduled ? (
            <p className="mt-1">{estimate.effectiveOn ? `${formatDay(estimate.effectiveOn)}から` : "次回更新時から"}{kintonePlanLabel(selectedGrade.kintonePlan)}・{selectedGrade.appLimit}アプリ・{cycle === "monthly" ? "月払い" : "年払い"}に変わります。それまでは今のプランのままで、今回のお支払いはありません。</p>
          ) : (
            <>
              <p className="mt-1">今回のお支払い: <strong>{yen(estimate.chargeAmountWithTax)}</strong>（税込。うち消費税 {yen(estimate.taxAmount)}）</p>
              {recurringAmount !== null && estimate.changeKind !== "immediate_upgrade"
                ? <p>以降は{cycle === "monthly" ? "毎月" : "毎年"} {yen(recurringAmount)}（税込）{method === "invoice" ? "の請求書を更新日に発行します。" : "を自動で更新します。"}</p>
                : null}
              {estimate.changeKind === "immediate_upgrade" ? <p>今の期間の残りの差額です。次回の更新から新しい料金になります。</p> : null}
              {invoiceOffered ? (
                <fieldset className="mt-3" aria-label="お支払い方法">
                  <legend className="mb-1 text-xs text-muted-foreground">お支払い方法</legend>
                  <div className="flex flex-wrap gap-2">
                    {(["card", "invoice"] as const).map((value) => (
                      <label key={value} className={`relative cursor-pointer rounded-md border bg-background px-3 py-1.5 text-sm ${chosenMethod === value ? "border-primary bg-primary/10 font-medium" : ""}`}>
                        <input type="radio" name="payment-method" value={value} className="sr-only" checked={chosenMethod === value} onChange={() => setChosenMethod(value)} />
                        {PAYMENT_METHOD_LABELS[value]}
                      </label>
                    ))}
                  </div>
                  {chosenMethod === "invoice" ? <p className="mt-1 text-xs text-muted-foreground">請求書を発行すると、すぐに使えます。お支払いは銀行振込かコンビニ払いです。</p> : null}
                </fieldset>
              ) : onInvoiceContract ? <p className="mt-1 text-muted-foreground">お支払い方法: 請求書払い（銀行振込・コンビニ）</p> : null}
            </>
          )}
          {blockedByOpenInvoice ? (
            <p role="alert" className="mt-3 text-destructive">未払いの請求書があるため、お支払い後に変更できます。</p>
          ) : (
            <Button className="mt-3" onClick={() => setConfirming(true)} disabled={busy}>
              {scheduled ? "変更内容を確認する" : "お申込み内容を確認する"}
            </Button>
          )}
        </div>
      ) : null}

      {estimate && selectedGrade && confirming ? (
        <div className="rounded-lg border border-primary/40 bg-primary/5 p-4 text-sm">
          <p className="font-semibold">最終確認</p>
          <dl className="mt-2 grid gap-1 sm:grid-cols-[8rem_1fr]">
            <dt className="text-muted-foreground">プラン</dt>
            <dd>{kintonePlanLabel(selectedGrade.kintonePlan)}（自己申告）・{selectedGrade.appLimit}アプリまで・{cycle === "monthly" ? "月払い" : "年払い"}</dd>
            {scheduled ? (
              <>
                <dt className="text-muted-foreground">変更日</dt>
                <dd>{estimate.effectiveOn ? formatDay(estimate.effectiveOn) : "次回更新日"}</dd>
              </>
            ) : byInvoice ? (
              <>
                <dt className="text-muted-foreground">お支払い方法</dt>
                <dd>{PAYMENT_METHOD_LABELS.invoice}</dd>
                <dt className="text-muted-foreground">今回の請求額</dt>
                <dd>{yen(estimate.chargeAmountWithTax)}（税込）</dd>
                <dt className="text-muted-foreground">支払期限</dt>
                <dd>{invoiceDueText(estimate.changeKind, billing?.billingAnchorDay ?? null)}</dd>
                <dt className="text-muted-foreground">振込手数料</dt>
                <dd>お客様のご負担です。</dd>
                <dt className="text-muted-foreground">利用開始</dt>
                <dd>請求書を発行した時点から使えます。</dd>
                <dt className="text-muted-foreground">更新</dt>
                <dd>
                  {estimate.changeKind === "immediate_upgrade"
                    ? "次回の更新から新しい料金の請求書になります。"
                    : `${cycle === "monthly" ? "毎月" : "毎年"}、更新日に次の請求書を発行します。`}
                  解約すると期間の終わりで終了し、日割りの返金はありません。
                </dd>
              </>
            ) : (
              <>
                <dt className="text-muted-foreground">今回のお支払い</dt>
                <dd>{yen(estimate.chargeAmountWithTax)}（税込）</dd>
                <dt className="text-muted-foreground">更新</dt>
                <dd>{cycle === "monthly" ? "毎月" : "毎年"}自動で更新します。解約すると期間の終わりで終了し、日割りの返金はありません。</dd>
              </>
            )}
          </dl>
          {byInvoice ? (
            <div className="mt-3 space-y-2">
              {konbiniUnavailable ? (
                <p className="rounded-md border border-amber-300 bg-amber-50 p-2 text-amber-950">
                  今回の請求額は{yen(catalog.invoicePayment.konbiniMaxAmountWithTax)}を超えるため、コンビニ払いは使えません。銀行振込でお支払いください。
                </p>
              ) : null}
              <div>
                <p className="font-medium">お支払いが確認できないとき</p>
                <ul className="mt-1 list-disc space-y-1 pl-5">
                  <li>支払期限の翌日に、お支払いのお願いをメールでお送りします。</li>
                  <li>支払期限の8日後に、サービスを停止します。お支払いを確認できると、すぐに再開します。</li>
                  <li>停止から30日たってもお支払いがないときは、契約を自動で解約します。解約後も、請求額のお支払いは必要です。</li>
                  <li>自動で解約しても、データは削除しません。</li>
                </ul>
              </div>
            </div>
          ) : null}
          {scheduled ? null : (
            <label className="mt-3 flex items-start gap-2">
              <input type="checkbox" className="mt-1" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} />
              <span>
                {catalog.paidTermsUrl
                  ? <a href={catalog.paidTermsUrl} target="_blank" rel="noreferrer" className="text-primary underline">有料プランの利用条件（版 {catalog.paidTermsVersion}）</a>
                  : <>有料プランの利用条件（版 {catalog.paidTermsVersion}）</>}
                を確認し、同意します。
              </span>
            </label>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={() => void proceed()} disabled={busy || (!scheduled && !agreed)}>
              {scheduled ? "変更を予約する" : byInvoice ? "請求書を発行して申し込む" : "決済へ進む"}
            </Button>
            <Button variant="outline" onClick={() => setConfirming(false)} disabled={busy}>戻る</Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** D9: the due date is the day before the anchor day of next month; the invoice shows the exact date. */
function invoiceDueText(changeKind: PlanChangeEstimateResponse["changeKind"], anchorDay: number | null) {
  if (changeKind === "immediate_upgrade" && anchorDay) {
    return `翌月${anchorDay}日（契約の基準日）の前日です。日付は請求書に記載します。`;
  }
  return "翌月の、お申込み日と同じ日の前日です。日付は請求書に記載します。";
}

export function kintonePlanLabel(value: PaidCatalogGrade["kintonePlan"]) {
  return value === "standard" ? "スタンダードコース" : "ワイドコース";
}

function yen(value: number) {
  return new Intl.NumberFormat("ja-JP", { style: "currency", currency: "JPY" }).format(value);
}
